'use client';

import { Suspense, lazy, useEffect } from 'react';
import AppFrame, { NotFoundContent } from './AppFrame';
import { Navigate, useLocation } from './router/nextCompat';
import { isSupportedLanguage, stripLanguagePrefix } from './i18n/urlLanguage';
import { useI18n } from './context/LanguageContext';
import { REFUND_POLICY_DOCUMENTS } from './locales/refundPolicy';
import type { StaticPageDocument } from './locales/staticPages';
import { reactHubPathToEmbedSrc } from './constants/hubLinks';

const SkoleomNotFoundPage = lazy(() => import('./components/error/SkoleomNotFoundPage'));
const BusinessPage = lazy(() => import('./views/public/BusinessPage'));
const ExternalArticlePage = lazy(() => import('./views/public/ExternalArticlePage'));
const HomePage = lazy(() => import('./views/public/HomePage'));
const NewsArticlePage = lazy(() => import('./views/public/NewsArticlePage'));
const NewsPage = lazy(() => import('./views/public/NewsPage'));
const PiecePage = lazy(() => import('./views/public/PiecePage'));
const StoresPage = lazy(() => import('./views/public/StoresPage'));
const SubscriptionPage = lazy(() => import('./views/public/SubscriptionPage'));
const StaticDocumentPage = lazy(() => import('./views/public/StaticDocumentPage'));

function ExternalContactRedirect() {
  useEffect(() => {
    window.location.replace('https://skoleom.com/contact');
  }, []);

  return null;
}

function LegalDocumentPage({ document }: { document: StaticPageDocument }) {
  return (
    <div className="sk-shell pt-32 pb-20">
      <div className="mx-auto max-w-[1100px] px-4 sm:px-6 lg:px-8">
        <section className="rounded-[1.75rem] border border-white/10 bg-[#0b0b0b] p-6 sm:p-8 lg:p-10">
          <p className="inline-flex rounded-full border border-white/12 bg-white/5 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-white/55">
            {document.eyebrow}
          </p>
          <h1 className="mt-5 font-display text-[clamp(2.2rem,5vw,4.8rem)] uppercase leading-[0.92] tracking-tight">
            {document.title}
          </h1>
          <p className="mt-4 max-w-[64ch] text-sm leading-relaxed text-white/72 sm:text-base">
            {document.intro}
          </p>
          {document.meta ? (
            <p className="mt-4 text-[11px] font-medium uppercase tracking-[0.18em] text-skoleom-lime">
              {document.meta}
            </p>
          ) : null}
        </section>

        <div className="mt-8 space-y-5">
          {document.sections.map((section) => (
            <section
              key={section.title}
              className="rounded-[1.4rem] border border-white/10 bg-[#0b0b0b] p-6 sm:p-7"
            >
              <h2 className="text-xl font-bold uppercase tracking-wide text-white">
                {section.title}
              </h2>
              {section.paragraphs?.map((paragraph: string) => (
                <p
                  key={paragraph}
                  className="mt-4 text-sm leading-relaxed text-white/72 sm:text-base"
                >
                  {paragraph}
                </p>
              ))}
              {section.items?.length ? (
                <ul className="mt-4 space-y-3 text-sm leading-relaxed text-white/72 sm:text-base">
                  {section.items.map((item: string) => (
                    <li
                      key={item}
                      className="rounded-2xl border border-white/8 bg-white/[0.03] px-4 py-3"
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

function RefundPage() {
  const { language } = useI18n();
  const document = REFUND_POLICY_DOCUMENTS[language] ?? REFUND_POLICY_DOCUMENTS.en;

  return <LegalDocumentPage document={document} />;
}

function HubStaticPage() {
  const location = useLocation();
  const src = reactHubPathToEmbedSrc(location.pathname, location.search, location.hash);

  if (!src) return <Navigate to="/" replace />;

  return (
    <iframe
      title="Skoleom footer page"
      src={src}
      className="min-h-screen w-full border-0 bg-[#060608]"
    />
  );
}

function resolveSegments(slug?: string[], pathname?: string) {
  const fromPath = pathname ? stripLanguagePrefix(pathname).split('/').filter(Boolean) : [];
  const segments = slug?.length ? slug : fromPath;
  if (segments[0] && isSupportedLanguage(segments[0])) {
    return segments.slice(1);
  }
  return segments;
}

export default function PageResolver({ slug }: { slug?: string[] }) {
  const location = useLocation();
  const segments = resolveSegments(slug, location.pathname);
  const [first, second, third] = segments;
  let content: JSX.Element;

  if (!first) content = <HomePage />;
  else if (first === 'stores') content = <StoresPage />;
  else if (first === 'piece' && second) content = <PiecePage />;
  else if (first === 'about-technology') content = <StaticDocumentPage documentKey="technology" />;
  else if (first === 'subscription' || first === 'abonnement') content = <SubscriptionPage />;
  else if (first === 'news' && second === 'external' && third) content = <ExternalArticlePage />;
  else if (first === 'news' && second) content = <NewsArticlePage />;
  else if (first === 'news') content = <NewsPage />;
  else if (first === 'contact') content = <ExternalContactRedirect />;
  else if (first === 'music') content = <BusinessPage />;
  else if (first === 'mission') content = <StaticDocumentPage documentKey="mission" />;
  else if (first === 'funding') content = <StaticDocumentPage documentKey="funding" />;
  else if (first === 'patents') content = <StaticDocumentPage documentKey="patents" />;
  else if (first === 'legal') content = <StaticDocumentPage documentKey="legal" />;
  else if (first === 'terms') content = <StaticDocumentPage documentKey="terms" />;
  else if (first === 'privacy') content = <StaticDocumentPage documentKey="privacy" />;
  else if (first === 'affiliates') content = <StaticDocumentPage documentKey="affiliates" />;
  else if (first === 'cookies') content = <StaticDocumentPage documentKey="cookies" />;
  else if (first === 'refund') content = <RefundPage />;
  else if (first === 'hub' && second) content = <HubStaticPage />;
  else if (first === '404' || first === 'not-found') content = <SkoleomNotFoundPage />;
  else if (first === 'business') content = <Navigate to="/music" replace />;
  else if (first === 'careers') content = <Navigate to="/contact" replace />;
  else if (first === 'investors') content = <Navigate to="/funding" replace />;
  else if (first === 'press') content = <Navigate to="/news" replace />;
  else content = <NotFoundContent />;

  return (
    <AppFrame>
      <Suspense fallback={null}>{content}</Suspense>
    </AppFrame>
  );
}
